import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  closeRestaurantRfq,
  confirmRfqCompletion,
  getRestaurantRfq,
  listRestaurantRfqs,
  respondRfqSubstitution,
} from '../api/rfqs'
import {
  completeSupplierRfq,
  decideSupplierRfq,
  listSupplierInbox,
} from '../api/supplierInbox'
import type {
  RfqDto,
  SupplierRfqInboxItemDto,
  SupplierRfqRejectionReasonCode,
} from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { useBuyerEntitlements } from '../hooks/useBuyerEntitlements'
import { useActiveVenue } from '../venues/ActiveVenueProvider'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { useToast } from '../components/ToastProvider'
import { OrdersListPanel } from './OrdersListPanel'
import { RestaurantOrderDetailPanel } from './RestaurantOrderDetailPanel'
import { SupplierOrderDetailPanel } from './SupplierOrderDetailPanel'
import {
  isArchivedRestaurantRfq,
  isArchivedSupplierInbox,
  isRestaurantActionFilter,
  needsRestaurantAction,
  restaurantDisplayName,
} from './ordersUtils'

type ViewState =
  | { kind: 'list' }
  | { kind: 'restaurant-detail'; rfq: RfqDto }
  | { kind: 'supplier-detail'; item: SupplierRfqInboxItemDto }

type OrdersScreenProps = {
  /** Rendered inside RequestScreen history tab (no page header / new-RFQ CTA). */
  embedded?: boolean
}

export const OrdersScreen = ({ embedded = false }: OrdersScreenProps) => {
  const { session, role, loading: authLoading } = useAuth()
  const entitlements = useBuyerEntitlements()
  const { activeVenueId } = useActiveVenue()
  const navigate = useNavigate()
  const location = useLocation()
  const { showToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const rfqIdParam = searchParams.get('rfqId')
  const initialRfqId = rfqIdParam ? Number(rfqIdParam) : null
  const flashShownRef = useRef<string | null>(null)

  const onOpenChat = (counterpartyOrganizationId: number, counterpartyName: string) => {
    navigate('/chat', {
      state: { partnerId: counterpartyOrganizationId, partnerName: counterpartyName },
    })
  }

  const [view, setView] = useState<ViewState>({ kind: 'list' })
  const [restaurantOrders, setRestaurantOrders] = useState<RfqDto[]>([])
  const [supplierOrders, setSupplierOrders] = useState<SupplierRfqInboxItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [supplierMessage, setSupplierMessage] = useState('')
  const [archiveExpanded, setArchiveExpanded] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState<SupplierRfqRejectionReasonCode | null>(null)
  const [rejectMessage, setRejectMessage] = useState('')
  const [disputeOpen, setDisputeOpen] = useState(false)
  const [disputeComment, setDisputeComment] = useState('')
  const [substitutionOpen, setSubstitutionOpen] = useState(false)
  const [substitutionMessage, setSubstitutionMessage] = useState('')
  const [excludeMode, setExcludeMode] = useState(false)
  const [excludedLineIds, setExcludedLineIds] = useState<number[]>([])

  const organizationId = session?.organizationId ?? 0
  const isSupplier = role === 'supplier'
  const actionFilter = !isSupplier && isRestaurantActionFilter(searchParams.get('filter'))

  const { activeRestaurantOrders, archivedRestaurantOrders } = useMemo(() => {
    const active: RfqDto[] = []
    const archived: RfqDto[] = []
    for (const rfq of restaurantOrders) {
      if (isArchivedRestaurantRfq(rfq)) {
        archived.push(rfq)
      } else {
        active.push(rfq)
      }
    }
    if (actionFilter) {
      return {
        activeRestaurantOrders: active.filter(needsRestaurantAction),
        archivedRestaurantOrders: [],
      }
    }
    return { activeRestaurantOrders: active, archivedRestaurantOrders: archived }
  }, [restaurantOrders, actionFilter])

  const { activeSupplierOrders, archivedSupplierOrders } = useMemo(() => {
    const active: SupplierRfqInboxItemDto[] = []
    const archived: SupplierRfqInboxItemDto[] = []
    for (const item of supplierOrders) {
      if (isArchivedSupplierInbox(item)) {
        archived.push(item)
      } else {
        active.push(item)
      }
    }
    return { activeSupplierOrders: active, archivedSupplierOrders: archived }
  }, [supplierOrders])

  useEffect(() => {
    const flash = (location.state as { flash?: string } | null)?.flash
    if (!flash || flashShownRef.current === flash) {
      return
    }
    flashShownRef.current = flash
    showToast(flash)
    navigate(location.pathname + location.search, { replace: true, state: {} })
  }, [location.pathname, location.search, location.state, navigate, showToast])

  const loadList = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      if (isSupplier) {
        const page = await listSupplierInbox(organizationId, 0, 200)
        setSupplierOrders(page.items)
      } else {
        const items = await listRestaurantRfqs(organizationId, activeVenueId)
        setRestaurantOrders(items)
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить заявки')
    } finally {
      setLoading(false)
    }
  }, [activeVenueId, isSupplier, organizationId])

  const refreshSupplierItem = useCallback(
    async (inboxId: number) => {
      const page = await listSupplierInbox(organizationId)
      const refreshed = page.items.find((entry) => entry.inboxId === inboxId)
      setSupplierOrders(page.items)
      if (refreshed) {
        setView({ kind: 'supplier-detail', item: refreshed })
      } else {
        setView({ kind: 'list' })
      }
    },
    [organizationId],
  )

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    if (!initialRfqId) {
      return
    }

    const openInitial = async () => {
      try {
        if (isSupplier) {
          const page = await listSupplierInbox(organizationId, 0, 200)
          const item = page.items.find((entry) => entry.rfqId === initialRfqId)
          if (item) {
            setView({ kind: 'supplier-detail', item })
          }
        } else {
          const rfq = await getRestaurantRfq(initialRfqId)
          setView({ kind: 'restaurant-detail', rfq })
        }
      } catch {
        // Deep-link fallback: stay on list if detail is unavailable.
      }
    }

    void openInitial()
  }, [initialRfqId, isSupplier, organizationId])

  const handleRestaurantCancel = async (rfq: RfqDto) => {
    if (!window.confirm('Отменить заявку? Поставщик увидит, что заявка закрыта.')) {
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      const updated = await closeRestaurantRfq(rfq.id)
      showToast('Заявка отменена')
      setView({ kind: 'restaurant-detail', rfq: updated })
      await loadList()
    } catch (cancelError) {
      setError(cancelError instanceof Error ? cancelError.message : 'Не удалось отменить заявку')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierAccept = async (item: SupplierRfqInboxItemDto) => {
    setActionLoading(true)
    setError(null)

    try {
      await decideSupplierRfq(item.inboxId, organizationId, 'accept', {
        message: supplierMessage,
      })
      setSupplierMessage('')
      showToast('Заявка принята')
      await refreshSupplierItem(item.inboxId)
    } catch (decisionError) {
      setError(decisionError instanceof Error ? decisionError.message : 'Не удалось принять заявку')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierReject = async (item: SupplierRfqInboxItemDto) => {
    if (!rejectReason) {
      setError('Выберите причину отказа')
      return
    }
    if (rejectReason === 'other' && !rejectMessage.trim()) {
      setError('Опишите причину отказа')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      await decideSupplierRfq(item.inboxId, organizationId, 'reject', {
        message: rejectMessage || supplierMessage,
        reasonCode: rejectReason,
      })
      setSupplierMessage('')
      setRejectMessage('')
      setRejectReason(null)
      setRejectOpen(false)
      showToast('Заявка отклонена')
      await refreshSupplierItem(item.inboxId)
    } catch (decisionError) {
      setError(decisionError instanceof Error ? decisionError.message : 'Не удалось отклонить заявку')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierComplete = async (item: SupplierRfqInboxItemDto) => {
    setActionLoading(true)
    setError(null)

    try {
      await completeSupplierRfq(item.inboxId, organizationId, supplierMessage)
      setSupplierMessage('')
      showToast('Выполнение отмечено')
      await refreshSupplierItem(item.inboxId)
    } catch (completeError) {
      setError(
        completeError instanceof Error ? completeError.message : 'Не удалось отметить выполнение',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierProposeSubstitution = async (item: SupplierRfqInboxItemDto) => {
    const message = substitutionMessage.trim()
    if (message.length < 10) {
      setError('Опишите предлагаемую замену (не менее 10 символов)')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      await decideSupplierRfq(item.inboxId, organizationId, 'propose_substitution', { message })
      setSubstitutionMessage('')
      setSubstitutionOpen(false)
      setSupplierMessage('')
      showToast('Предложение замены отправлено')
      await refreshSupplierItem(item.inboxId)
    } catch (proposalError) {
      setError(
        proposalError instanceof Error ? proposalError.message : 'Не удалось отправить предложение замены',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleRestaurantAcceptSubstitution = async (rfq: RfqDto) => {
    setActionLoading(true)
    setError(null)

    try {
      const updated = await respondRfqSubstitution(rfq.id, 'accept')
      setExcludeMode(false)
      setExcludedLineIds([])
      showToast('Замена принята')
      setView({ kind: 'restaurant-detail', rfq: updated })
      await loadList()
    } catch (acceptError) {
      setError(
        acceptError instanceof Error ? acceptError.message : 'Не удалось принять замену',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleRestaurantExcludeLines = async (rfq: RfqDto) => {
    if (excludedLineIds.length === 0) {
      setError('Выберите позиции, которые нужно исключить из заявки')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      const updated = await respondRfqSubstitution(rfq.id, 'exclude_lines', {
        excludedLineIds,
      })
      setExcludeMode(false)
      setExcludedLineIds([])
      showToast('Позиции исключены из заявки')
      setView({ kind: 'restaurant-detail', rfq: updated })
      await loadList()
    } catch (excludeError) {
      setError(
        excludeError instanceof Error ? excludeError.message : 'Не удалось исключить позиции',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleRestaurantCancelSubstitution = async (rfq: RfqDto) => {
    if (!window.confirm('Отказаться от заявки? Заявка будет закрыта.')) {
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      const updated = await respondRfqSubstitution(rfq.id, 'cancel')
      setExcludeMode(false)
      setExcludedLineIds([])
      showToast('Заявка отменена')
      setView({ kind: 'restaurant-detail', rfq: updated })
      await loadList()
    } catch (cancelError) {
      setError(
        cancelError instanceof Error ? cancelError.message : 'Не удалось отказаться от заявки',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const toggleExcludedLine = (lineId: number) => {
    setExcludedLineIds((current) =>
      current.includes(lineId) ? current.filter((id) => id !== lineId) : [...current, lineId],
    )
  }

  const handleRestaurantConfirm = async (rfq: RfqDto) => {
    setActionLoading(true)
    setError(null)

    try {
      const updated = await confirmRfqCompletion(rfq.id, 'confirm')
      showToast('Выполнение подтверждено')
      setView({ kind: 'restaurant-detail', rfq: updated })
      await loadList()
    } catch (confirmError) {
      setError(
        confirmError instanceof Error ? confirmError.message : 'Не удалось подтвердить выполнение',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleRestaurantDispute = async (rfq: RfqDto) => {
    if (!disputeComment.trim()) {
      setError('Опишите замечания к поставке')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      const updated = await confirmRfqCompletion(rfq.id, 'dispute', disputeComment)
      setDisputeComment('')
      setDisputeOpen(false)
      showToast('Замечания отправлены')
      setView({ kind: 'restaurant-detail', rfq: updated })
      await loadList()
    } catch (disputeError) {
      setError(disputeError instanceof Error ? disputeError.message : 'Не удалось отправить замечания')
    } finally {
      setActionLoading(false)
    }
  }

  const activeCount = isSupplier ? activeSupplierOrders.length : activeRestaurantOrders.length
  const archivedCount = isSupplier ? archivedSupplierOrders.length : archivedRestaurantOrders.length
  const totalCount = activeCount + archivedCount

  const handleClearActionFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('filter')
    setSearchParams(next, { replace: true })
  }

  const ordersPageHeader = embedded ? (
    <p className="echat-muted market-request-history-lead">
      Ранее сформированные заявки и их статусы.
    </p>
  ) : (
    <MarketPageHeader
      title={isSupplier ? 'Входящие запросы' : 'Заявки'}
      description={
        isSupplier
          ? 'Заявки от ресторанов, которые ждут вашего решения.'
          : 'Отслеживайте статусы и отвечайте на предложения поставщиков.'
      }
      actions={
        !isSupplier ? (
          <Link to="/orders/create" className="echat-btn echat-btn--primary">
            Новая заявка
          </Link>
        ) : undefined
      }
    />
  )

  const listPanel = (
    <OrdersListPanel
      isSupplier={isSupplier}
      isStartPlan={entitlements.isStartPlan}
      actionFilter={actionFilter}
      error={error}
      archiveExpanded={archiveExpanded}
      activeCount={activeCount}
      archivedCount={archivedCount}
      totalCount={totalCount}
      activeSupplierOrders={activeSupplierOrders}
      archivedSupplierOrders={archivedSupplierOrders}
      activeRestaurantOrders={activeRestaurantOrders}
      archivedRestaurantOrders={archivedRestaurantOrders}
      selectedSupplierInboxId={view.kind === 'supplier-detail' ? view.item.inboxId : null}
      selectedRestaurantRfqId={view.kind === 'restaurant-detail' ? view.rfq.id : null}
      onSelectSupplierItem={(item) => setView({ kind: 'supplier-detail', item })}
      onSelectRestaurantRfq={(rfq) => setView({ kind: 'restaurant-detail', rfq })}
      onClearActionFilter={handleClearActionFilter}
      onToggleArchive={() => setArchiveExpanded((expanded) => !expanded)}
    />
  )

  const renderOrdersSplit = (detail: ReactNode, detailOpen = false) => {
    const body = (
      <>
        {ordersPageHeader}
        <div className={`market-orders-split${detailOpen ? ' market-orders-split--detail-open' : ''}`}>
          <aside className="market-orders-split__list">{listPanel}</aside>
          <main className="market-orders-split__detail">{detail}</main>
        </div>
      </>
    )
    if (embedded) {
      return <div className="market-request-history">{body}</div>
    }
    return <section className="market-page market-page--orders">{body}</section>
  }

  if (authLoading || loading) {
    return renderOrdersSplit(<p className="echat-orders__status">Загрузка заявок…</p>, false)
  }

  if (view.kind === 'restaurant-detail') {
    const { rfq } = view

    return renderOrdersSplit(
      <RestaurantOrderDetailPanel
        rfq={rfq}
        error={error}
        actionLoading={actionLoading}
        excludeMode={excludeMode}
        excludedLineIds={excludedLineIds}
        disputeOpen={disputeOpen}
        disputeComment={disputeComment}
        onBack={() => setView({ kind: 'list' })}
        onToggleExcludedLine={toggleExcludedLine}
        onAcceptSubstitution={() => void handleRestaurantAcceptSubstitution(rfq)}
        onStartExcludeMode={() => {
          setExcludeMode(true)
          setExcludedLineIds([])
          setError(null)
        }}
        onCancelExcludeMode={() => {
          setExcludeMode(false)
          setExcludedLineIds([])
        }}
        onExcludeLines={() => void handleRestaurantExcludeLines(rfq)}
        onCancelSubstitution={() => void handleRestaurantCancelSubstitution(rfq)}
        onConfirmCompletion={() => void handleRestaurantConfirm(rfq)}
        onStartDispute={() => setDisputeOpen(true)}
        onCancelDispute={() => {
          setDisputeOpen(false)
          setDisputeComment('')
        }}
        onDisputeCommentChange={setDisputeComment}
        onSubmitDispute={() => void handleRestaurantDispute(rfq)}
        onOpenChat={() =>
          onOpenChat(
            rfq.supplierOrganizationId,
            rfq.supplierContact?.supplierName?.trim() || `Поставщик #${rfq.supplierOrganizationId}`,
          )
        }
        onCancelOrder={() => void handleRestaurantCancel(rfq)}
      />,
      true,
    )
  }

  if (view.kind === 'supplier-detail') {
    const { item } = view

    return renderOrdersSplit(
      <SupplierOrderDetailPanel
        item={item}
        error={error}
        actionLoading={actionLoading}
        supplierMessage={supplierMessage}
        onSupplierMessageChange={setSupplierMessage}
        onBack={() => setView({ kind: 'list' })}
        onAccept={() => void handleSupplierAccept(item)}
        onComplete={() => void handleSupplierComplete(item)}
        onOpenChat={() => onOpenChat(item.restaurantOrganizationId, restaurantDisplayName(item))}
        rejectOpen={rejectOpen}
        rejectReason={rejectReason}
        rejectMessage={rejectMessage}
        onOpenReject={() => {
          setRejectOpen(true)
          setError(null)
        }}
        onSelectRejectReason={setRejectReason}
        onRejectMessageChange={setRejectMessage}
        onConfirmReject={() => void handleSupplierReject(item)}
        onCancelReject={() => {
          setRejectOpen(false)
          setRejectReason(null)
          setRejectMessage('')
        }}
        substitutionOpen={substitutionOpen}
        substitutionMessage={substitutionMessage}
        onOpenSubstitution={() => {
          setSubstitutionOpen(true)
          setError(null)
        }}
        onSubstitutionMessageChange={setSubstitutionMessage}
        onConfirmSubstitution={() => void handleSupplierProposeSubstitution(item)}
        onCancelSubstitution={() => {
          setSubstitutionOpen(false)
          setSubstitutionMessage('')
        }}
      />,
      true,
    )
  }

  return renderOrdersSplit(
    <MarketEmptyState
      title="Выберите заявку"
      description="Список слева — детали откроются здесь"
    />,
    false,
  )
}
